import { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import { CollaboratorChip } from "../../../components/core/CollaboratorChip/CollaboratorChip";
import { ChipSizeEnum } from "../../../components/core/Chip/Chip";
import { IconManager } from "../../../components/core/IconManager/IconManager";
import { Loader } from "../../../components/core/Loader/Loader";
import { Pagination } from "../../../components/core/Pagination/Pagination";
import SearchInput from "../../../components/core/SearchInput/SearchInput";
import { TitlesTyp } from "../../../components/Typography/Typography";
import { MATRIX_COLLABORATORS_PAGE_SIZE } from "../../../constants/AppConstants";
import {
  EMPTY_STRING,
  RESULTS_LABEL,
  NAME_SURNAME_EMAIL_LABEL,
} from "../../../constants/Dictionary";
import {
  internalGradeColors,
  MatrixSizes,
} from "../../../constants/PotentialMatrixConstants";
import { useAppSelector } from "../../../hooks/store.hook";
import { MatrixItem } from "../../../types/api/potentialMatrix.type";
import {
  FontWeightEnum,
  IconSizeEnum,
  CollaboratorInfo,
} from "../../../types/AppTypes";
import {
  StyledMatrixDetails,
  StyledDetailsHeader,
  StyledMatrixDetailsContent,
} from "../../PotentialMatrixV3/DevelopmentMatrixDetails/DevelopmentMatrixDetails.styled";
import { ReactComponent as CloseIcon } from "../../../icons/close.svg";
import { Matrix } from "../../../components/Matrix/Matrix";
import { PotentialMatrixFilters } from "../../../hooks/usePotentialMatrixFilters.hook";
import { usePotentialMatrixDetails } from "../../../hooks/potentialMatrix.hook";

type MatrixDetailsProps = {
  item: MatrixItem;
  onClose: () => void;
  filters: PotentialMatrixFilters;
  yearSelected: number;
  onClickResume: (collaborator: CollaboratorInfo) => void;
  matrixData: MatrixItem[];
};

export const MatrixDetails = ({
  item,
  onClose,
  filters,
  yearSelected,
  onClickResume,
  matrixData,
}: MatrixDetailsProps) => {
  const { config } = useAppSelector((state) => state.config);

  const [searchValue, setSearchValue] = useState("");
  const [searchDebouncedValue] = useDebounce(searchValue, 500);
  const [pageNumber, setPageNumber] = useState(0);

  const potentialMatrixConfig = config.find(
    (item) => item.year === yearSelected,
  )?.config?.finalInterviewDimensions;

  const configItem = potentialMatrixConfig?.find(
    (c) => c.id === item?.dimensionId,
  );

  const { data, isFetching } = usePotentialMatrixDetails({
    enabled: !!item,
    searchDebouncedValue,
    item,
    pageNumber,
    filters,
  });

  const getText = () => {
    if (!item) {
      return "";
    }

    if (configItem?.range.length === 2) {
      return `Collaboratori in ${item.screen.toUpperCase()} | ${configItem.range[0]}-${configItem.range[1]}%`;
    }

    return `Collaboratori in ${item.screen.toUpperCase()} | ${EMPTY_STRING} `;
  };

  useEffect(() => {
    setPageNumber(0);
  }, [searchDebouncedValue, filters]);

  return (
    <StyledMatrixDetailsContent $visible={!!item}>
      <div className="first-column">
        <div className="matrix-container">
          <Matrix
            data={matrixData}
            size={MatrixSizes.SMALL}
            yearSelected={yearSelected}
            {...(item && { selectedItem: item })}
          />
        </div>
      </div>
      <StyledMatrixDetails>
        <StyledDetailsHeader>
          <TitlesTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="3">
            {getText()}
          </TitlesTyp>
          <IconManager onClick={onClose} size={IconSizeEnum.MEDIUM}>
            <CloseIcon />
          </IconManager>
        </StyledDetailsHeader>
        {item ? (
          <>
            <StyledDetailsHeader>
              <TitlesTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="3">
                {isFetching ? "" : `${data?.pagination.total} ${RESULTS_LABEL}`}
              </TitlesTyp>
              <div className="search-container">
                <SearchInput
                  value={searchValue}
                  onChange={setSearchValue}
                  label={NAME_SURNAME_EMAIL_LABEL}
                  name={"search"}
                />
              </div>
            </StyledDetailsHeader>
            <Loader loading={isFetching} className="profiles-container">
              <div className="profiles-grid">
                {data?.data.map((collaboratorCalibration) => {
                  return (
                    <CollaboratorChip
                      key={collaboratorCalibration.collaborator.id}
                      collaborator={collaboratorCalibration.collaborator}
                      size={ChipSizeEnum.BIG}
                      onClick={onClickResume}
                      {...(collaboratorCalibration.collaborator
                        .internalGrade && {
                        borderColor:
                          internalGradeColors[
                            collaboratorCalibration.collaborator.internalGrade
                          ],
                      })}
                    />
                  );
                })}
              </div>
            </Loader>
            <Pagination
              current={pageNumber + 1}
              total={data?.pagination.total ?? 0}
              onChange={(pageNumber) => setPageNumber(pageNumber - 1)}
              pageSize={MATRIX_COLLABORATORS_PAGE_SIZE}
            />
          </>
        ) : null}
      </StyledMatrixDetails>
    </StyledMatrixDetailsContent>
  );
};
